import { makeAutoObservable } from 'mobx'
import * as moment from 'moment'
import { INTERNAL_BETA_TENANT_IDS, SUBSCRIPTION_LICENSE_TYPE, SUBSCRIPTION_STATUS } from '../constants/tenantConstants'
import { SCOPED_ADMIN_FLAG_KEYS, SCOPED_ADMIN_FLAG_PERMISSIONS } from '../constants/scopedAdminFlags'

export default class User {
  static PEOPLE_SCOPES = ['People.Read', 'User.ReadBasic.All']
  static MEETING_SCOPES = ['Calendars.Read.Shared', 'People.Read']

  user = {}
  name = 'John'
  mail = ''
  age = 42
  showAge = false
  isUserSet = false
  platform = ''
  displayName = ''
  graphAccessToken = ''
  oid = ''
  tenantId = ''
  isAdmin = false
  isGlobalAdmin = false
  isScopedAdmin = false
  isReportReader = false
  isConfigurationEditor = false
  userPrincipalName = ''
  tenantSettings = null
  tenant = null
  impersonateMode = false
  impersonateOldUser = null
  hasAdminConsent = null
  userSettings = {}
  canChangeGeneralSettings = SCOPED_ADMIN_FLAG_PERMISSIONS.canChangeGeneralSettings.defaultValue
  canAddRemoveUsers = SCOPED_ADMIN_FLAG_PERMISSIONS.canAddRemoveUsers.defaultValue
  canStartEditCycles = SCOPED_ADMIN_FLAG_PERMISSIONS.canStartEditCycles.defaultValue
  canEditUserAttributes = SCOPED_ADMIN_FLAG_PERMISSIONS.canEditUserAttributes.defaultValue
  canEditUserManager = SCOPED_ADMIN_FLAG_PERMISSIONS.canEditUserManager.defaultValue
  isPartner = false
  myPeopleErrorAccessDenied = false
  hasExternalUsers = false
  meetingErrorAccessDenied = false
  showAllowAccessBanner = false
  scopes = []

  constructor() {
    makeAutoObservable(this)
  }

  setUser(user) {
    this.displayName = user.displayName
    this.oid = user.oid
    this.graphAccessToken = user.graphAccessToken
    this.isUserSet = true
    this.userPrincipalName = user.userPrincipalName
    this.mail = user.mail
    this.tenantId = user.tenantId
    this.tenant = user.tenant
    this.tenantSettings = user.tenant.settings
    this.isGlobalAdmin = user.isGlobalAdmin ?? false
    this.isScopedAdmin = user.isScopedAdmin ?? false
    this.isReportReader = user.isReportReader ?? false
    this.isConfigurationEditor = user.isConfigurationEditor ?? false
    this.isAdmin = this.isGlobalAdmin

    if (user.tenant.partnerUTMCode) {
      this.isPartner = true
    }
    this.impersonateMode = user.impersonateMode
    this.impersonateOldUser = user.impersonateOldUser
    this.hasAdminConsent = user.hasAdminConsent
    this.userSettings = user.userSettings
    for (const key of SCOPED_ADMIN_FLAG_KEYS) {
      this[key] = user[key] !== undefined ? user[key] : SCOPED_ADMIN_FLAG_PERMISSIONS[key].defaultValue
    }

    if (moment().diff(moment(user.createdAt), 'hour', true) < 1) {
      //@ts-ignore
      if (window.clarity) {
        //@ts-ignore
        window.clarity('set', 'usertype', 'newuser')
      }
    }

    if (moment().diff(moment(user.tenant.createdAt), 'hour', true) < 1) {
      //@ts-ignore
      if (window.clarity) {
        //@ts-ignore
        window.clarity('set', 'usertype', 'newtenant')
      }
    }

    if (user.scp) {
      this.scopes = user.scp.split(' ')
      const scopeSet = new Set(this.scopes)
      this.myPeopleErrorAccessDenied = User.PEOPLE_SCOPES.some(scope => !scopeSet.has(scope))
      this.meetingErrorAccessDenied = User.MEETING_SCOPES.some(scope => !scopeSet.has(scope))
      this.showAllowAccessBanner = this.myPeopleErrorAccessDenied
    }
  }
  setTenantSettings(settings) {
    this.tenantSettings = settings
    this.tenant.settings = settings
  }

  setNotificationType(type) {
    this.tenantSettings.forceNotificationType = type
  }

  setHasExternalUsers(value) {
    this.hasExternalUsers = value
  }

  setUserLicenseModel(userLicenseModel) {
    this.tenant.userLicenseModel = userLicenseModel
  }

  setTenantSecurityGroup(secGroup) {
    this.tenant.userLicenseModelSecurityGroup = secGroup
  }

  setTenant(tenant) {
    this.tenant = tenant
  }
  setUserSettings(settings) {
    this.userSettings = settings
  }

  updateGraphAccessToken(newToken) {
    this.graphAccessToken = newToken
  }

  toggleAutoSyncUsers(value) {
    this.tenant.settings.autoSyncUsers = value
    this.tenantSettings.autoSyncUsers = value
  }

  setUserAccessNotification(value) {
    this.tenant.settings.userAccessNotification = value
    this.tenantSettings.userAccessNotification = value
  }

  setDefaultManagerInformationSource(value) {
    this.tenant.settings ??= {}
    this.tenant.settings.defaultManagerInformationSource = value
    if (this.tenantSettings) {
      this.tenantSettings.defaultManagerInformationSource = value
    }
  }

  updateBirthdayReminderSetting(settingKey, value) {
    this.tenant.settings ??= {}
    this.tenant.settings.birthdayReminder ??= {}
    this.tenantSettings.birthdayReminder ??= {}

    this.tenant.settings.birthdayReminder[settingKey] = value
    this.tenantSettings.birthdayReminder[settingKey] = value
  }

  hasModuleExtraBenefit(moduleKey) {
    // Check if module is available through trial subscription extra benefits
    const hasTrialBenefit =
      this.tenant.trialSubscription?.isExpired === false &&
      this.tenant.trialSubscription?.extraBenefits?.activatedFeatures?.includes(moduleKey)

    // Check if module is available through permanent tenant settings extra benefits
    const hasPermanentBenefit = this.tenant.settings?.extraBenefits?.activatedFeatures?.includes(moduleKey)

    return hasTrialBenefit || hasPermanentBenefit
  }

  isProfessionalSubscription() {
    return (
      this.isProfessionalTrial() ||
      (this.tenant.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE &&
        this.tenant.subscriptionLicenseType === SUBSCRIPTION_LICENSE_TYPE.PROFESSIONAL)
    )
  }

  hasAccessToModule(moduleKey) {
    return this.isProfessionalSubscription() || this.hasModuleExtraBenefit(moduleKey)
  }

  isNonTrialProfessionalSubscription() {
    return (
      this.tenant.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE &&
      this.tenant.subscriptionLicenseType === SUBSCRIPTION_LICENSE_TYPE.PROFESSIONAL
    )
  }

  isEssentialSubscription() {
    return (
      this.tenant.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE &&
      this.tenant.subscriptionLicenseType === SUBSCRIPTION_LICENSE_TYPE.ESSENTIAL
    )
  }

  isProfessionalTrial() {
    return (
      this.tenant.trialSubscription?.isExpired === false &&
      this.tenant.trialSubscription?.licenseType === SUBSCRIPTION_LICENSE_TYPE.PROFESSIONAL
    )
  }

  isStartEditCyclesEnabled() {
    return this.isScopedAdmin ? this.canStartEditCycles : true
  }

  isEditUserAttributesEnabled() {
    return this.isScopedAdmin ? this.canEditUserAttributes : true
  }

  isEditUserManagerEnabled() {
    return this.isScopedAdmin ? this.canEditUserManager : true
  }

  isAddRemoveUsersEnabled() {
    return this.isScopedAdmin ? this.canAddRemoveUsers : true
  }

  isChangeGeneralSettingsEnabled() {
    return this.isScopedAdmin ? this.canChangeGeneralSettings : true
  }

  isAiSettingEnabled = (settingName, isNegative = false) => {
    const { tenantSettings } = this

    if (!tenantSettings || tenantSettings.aiDisabled) {
      return false
    }

    const settingValue = tenantSettings?.[settingName]

    return isNegative ? !settingValue : settingValue
  }

  isBetaTenant = () => {
    return INTERNAL_BETA_TENANT_IDS.includes(this.tenantId)
  }
}
